import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Typography, Alert } from "@mui/material";

import LoadingButton from "../../components/ui/LoadingButton";
import Input from "../../components/ui/Input";
import { useIntl } from "../../contexts/IntlContext";
import { LayoutUnauthenticated } from "../../components/LayoutUnauthenticated";
import api from "../../services/api";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({ email: "" });
  const [message, setMessage] = useState("");
  const [sentEmail, setSentEmail] = useState("");

  const navigate = useNavigate();
  const intl = useIntl();

  const validateForm = () => {
    let isValid = true;
    const errors = { email: "" };
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      errors.email = intl.formatMessage({ id: 'login.emailRequired' });
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      errors.email = intl.formatMessage({ id: 'validation.invalidEmail' });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      await api.post('app/auth/forgot-password', {
        email: normalizedEmail,
      });

      setSentEmail(normalizedEmail);
      setEmail('');
      setMessage(intl.formatMessage({ id: 'forgotPassword.success' }));
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: 'forgotPassword.error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutUnauthenticated
      title={intl.formatMessage({ id: 'forgotPassword.title' })}
      subtitle={intl.formatMessage({ id: 'forgotPassword.subtitle' })}
    >
      {!message ? (
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Typography variant="h6">
            {intl.formatMessage({ id: 'forgotPassword.recover' })}
          </Typography>

          <Input
            label={intl.formatMessage({ id: 'common.email' })}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={formErrors.email !== ""}
            helperText={formErrors.email}
            slotProps={{
              formHelperText: { sx: { minHeight: '1.5em' } },
            }}
            sx={{ width: "100%", mb: 2 }}
          />

          <LoadingButton
            type="submit"
            color="primary"
            loading={loading}
            sx={{ width: "100%", mb: 2 }}
          >
            {intl.formatMessage({ id: 'common.continue' })}
          </LoadingButton>
        </form>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: 'forgotPassword.successTo' }, { email: sentEmail })}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {intl.formatMessage({ id: 'forgotPassword.spamHint' })}
          </Typography>
          <LoadingButton
            variant="text"
            onClick={() => setMessage("")}
            fullWidth={false}
            sx={{ mt: 1, p: 0, textDecoration: "underline", "&:hover": { textDecoration: "underline" } }}
          >
            {intl.formatMessage({ id: 'forgotPassword.sendToAnotherEmail' })}
          </LoadingButton>
        </Alert>
      )}
      <LoadingButton
        color="secondary"
        onClick={() => navigate("/login")}
        sx={{ width: "100%", mb: 2 }}
      >
        {intl.formatMessage({ id: 'common.back' })}
      </LoadingButton>
    </LayoutUnauthenticated>
  );
}
